import { Box, Card, CardContent } from '@mui/material';
import { PropsWithChildren } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useStatusViewContext } from '../../context/StatusViewContext';
import { Station } from '@app/core/types/stations';

export function StatusCard({
  station,
  innerRef,
  children,
}: PropsWithChildren<{
  station: Station;
  innerRef: (node?: Element | null) => void;
}>) {
  const { compact } = useStatusViewContext();
  const to = `/logger/s/${station.slug}`;

  return (
    <Card
      ref={innerRef}
      className="status-card"
      data-testid="station-status-card"
      data-station-name={station.name}
      elevation={2}
      sx={{
        position: 'relative',
        mb: compact ? 0 : 2, // No margin in grid (spacing handled by Grid), margin in list
        cursor: 'pointer',
        border: '1px solid transparent',
        transition: 'box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out',
        '&:hover': {
          boxShadow: 4,
          borderColor: 'primary.main',
        },
        height: compact ? '100%' : 'auto', // Full height in grid
        // When favorite is inactive, hide until card hover (pointer-events off when hidden).
        // Favorite (active) toggle has no --inactive class and stays visible.
        '& .status-card__favorite-toggle--inactive': {
          opacity: 0,
          pointerEvents: 'none',
          transition: 'opacity 0.1s ease-in-out',
        },
        '&:hover .status-card__favorite-toggle--inactive': {
          opacity: 1,
          pointerEvents: 'auto',
        },
      }}
    >
      {/* Stretch link: real <a> for URL preview without nesting anchors around child links. */}
      <Box
        component={RouterLink}
        to={to}
        className="status-card__link"
        sx={{ position: 'absolute', inset: 0, zIndex: 0 }}
      />
      <CardContent
        sx={{
          p: compact ? 1.5 : 2,
          position: 'relative',
          zIndex: 1,
          // Let clicks/hover on non-interactive areas hit the stretch link underneath.
          pointerEvents: 'none',
          // Waveform must receive hover for thumbnail timestamp preview.
          '& a, & button, & [role="button"], & .MuiButtonBase-root, & input, & label, & select, & textarea, & .status-card__waveform, & .status-card__shared-station-badge':
            {
              pointerEvents: 'auto',
            },
          // Keep inactive favorite non-interactive until the card hover rule re-enables it.
          '& .status-card__favorite-toggle--inactive': {
            pointerEvents: 'none',
          },
        }}
      >
        {children}
      </CardContent>
    </Card>
  );
}
